import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { v4 as uuidv4 } from "uuid";
import api from "../../Services/api";
import { HomeAppContext } from "../Home";

const filterOptions = ["all", "my", "shared"];

function buildConversationTitle(prompt, fallbackId) {
  const trimmed = prompt.trim();
  let title = trimmed.substring(0, 22);
  if (trimmed.length > 22) {
    const lastSpaceIndex = title.lastIndexOf(" ");
    if (lastSpaceIndex > 0) {
      title = title.substring(0, lastSpaceIndex);
    }
  }
  return title || `Conversation ${fallbackId.substring(0, 8)}`;
}

export default function useChatbot() {
  const { homeState } = useContext(HomeAppContext);

  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedConversationTitle, setSelectedConversationTitle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [aiEngines, setAiEngines] = useState([]);
  const [groupedEngines, setGroupedEngines] = useState({});
  const [engineFamilies, setEngineFamilies] = useState([]);
  const [selectedEngineFamily, setSelectedEngineFamily] = useState("");
  const [selectedAiEngine, setSelectedAiEngine] = useState("");
  const [activeTab, setActiveTab] = useState(1);
  const [connectionStatus, setConnectionStatus] = useState("connecting");
  const [userId, setUserId] = useState(null);

  const selectedConversationIdRef = useRef(selectedConversationId);
  const aiMessageIndexRef = useRef(null);
  const connectionRef = useRef(null);

  const selectedConversation = useMemo(
    () => conversations.find((conv) => conv.id === selectedConversationId),
    [conversations, selectedConversationId]
  );

  const messages = useMemo(
    () => (selectedConversation ? selectedConversation.messages : []),
    [selectedConversation]
  );

  const isReadOnly =
    selectedConversation?.userId !== homeState.userProfile?.userId &&
    selectedConversation?.shareType === 1;

  const fetchDefaultEngine = useCallback(async (familyName = "ChatGPT") => {
    try {
      const response = await api.post("/api/ai-engines/default", { name: familyName });
      const defaultEngine = response.data;
      if (defaultEngine) {
        setSelectedEngineFamily(defaultEngine.engineFamily);
        setSelectedAiEngine(defaultEngine.id);
      }
    } catch (error) {
      console.error("Error fetching default AI engine:", error);
    }
  }, []);

  const fetchAllEngines = useCallback(async () => {
    try {
      const response = await api.get("/api/ai-engines/ListActives");
      const engines = response.data.items || [];
      setAiEngines(engines);

      const grouped = engines.reduce((acc, engine) => {
        const family = engine.engineFamily;
        if (!acc[family]) acc[family] = [];
        acc[family].push(engine);
        return acc;
      }, {});

      setGroupedEngines(grouped);
      setEngineFamilies(Object.keys(grouped));
    } catch (error) {
      console.error("Error fetching AI engines:", error);
    }
  }, []);

  const fetchFilteredConversations = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await api.get("/api/chatbot/GetHistory", {
        params: { filter: filterOptions[activeTab] },
      });
      setConversations(response.data.history || []);
      setUserId(response.data.userId);
    } catch (error) {
      console.error("Error fetching filtered conversations:", error);
    } finally {
      setHistoryLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchDefaultEngine();
    fetchAllEngines();
  }, [fetchAllEngines, fetchDefaultEngine]);

  useEffect(() => {
    fetchFilteredConversations();
  }, [fetchFilteredConversations]);

  useEffect(() => {
    selectedConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId]);

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(process.env.REACT_APP_BACKEND_URL + "/chathub")
      .withAutomaticReconnect()
      .build();

    connectionRef.current = connection;

    connection
      .start()
      .then(() => {
        setConnectionStatus("connected");
        connection.on("ReceiveAiResponse", (messagePart) => {
          setConversations((prevConversations) =>
            prevConversations.map((conv) => {
              if (conv.id === selectedConversationIdRef.current) {
                const updatedMessages = [...conv.messages];
                const aiMessageIndex = aiMessageIndexRef.current;
                if (aiMessageIndex !== null && updatedMessages[aiMessageIndex]) {
                  updatedMessages[aiMessageIndex].text += messagePart;
                }
                return { ...conv, messages: updatedMessages };
              }
              return conv;
            })
          );
        });

        connection.on("SetConversationId", (conversationId) => {
          setSelectedConversationId(conversationId);
          selectedConversationIdRef.current = conversationId;
          setConversations((prev) =>
            prev.map((conv) => (conv.id === null ? { ...conv, id: conversationId } : conv))
          );
        });
      })
      .catch((error) => {
        console.error("SignalR Connection Error: ", error);
        setConnectionStatus("disconnected");
      });

    connection.onclose(() => setConnectionStatus("disconnected"));

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
      }
    };
  }, []);

  const handleEngineFamilyChange = useCallback(
    async (familyName) => {
      setSelectedEngineFamily(familyName);
      await fetchDefaultEngine(familyName);
      setSelectedConversationId(null);
      setSelectedConversationTitle(null);
    },
    [fetchDefaultEngine]
  );

  const handleAiEngineChange = useCallback((engineId) => {
    setSelectedAiEngine(engineId);
    setSelectedConversationId(null);
    setSelectedConversationTitle(null);
  }, []);

  const handleSelectConversation = useCallback(
    (conversationId) => {
      const selected = conversations.find((conv) => conv.id === conversationId);
      if (selected) {
        setSelectedConversationId(conversationId);
        setSelectedConversationTitle(selected.title);
        setSelectedAiEngine(selected.aiEngine);
      }
    },
    [conversations]
  );

  const handleNewConversation = useCallback(async () => {
    if (selectedEngineFamily) {
      await fetchDefaultEngine(selectedEngineFamily);
    }
    setSelectedConversationId(null);
    setSelectedConversationTitle(null);
  }, [fetchDefaultEngine, selectedEngineFamily]);

  const sendMessage = useCallback(
    async (messageText) => {
      if (!messageText || !messageText.trim() || !connectionRef.current || !selectedAiEngine) {
        return;
      }

      const trimmed = messageText.trim();
      const resolvedUserId = homeState.userProfile?.userId || userId;
      const now = new Date().toISOString();
      const userMessage = { role: "user", text: trimmed };
      const aiMessage = { role: "ai", text: "" };

      setLoading(true);

      try {
        if (!selectedConversationId) {
          const newConversationId = uuidv4();
          const title = buildConversationTitle(trimmed, newConversationId);
          const newConversation = {
            id: newConversationId,
            title,
            messages: [userMessage, aiMessage],
            aiEngine: selectedAiEngine,
            lastActivity: now,
            shareType: 0,
            userId: resolvedUserId,
          };

          setConversations((prev) => [newConversation, ...prev]);
          setSelectedConversationId(newConversation.id);
          setSelectedConversationTitle(title);
          aiMessageIndexRef.current = 1;

          await connectionRef.current.invoke("StartChat", {
            newMessage: trimmed,
            aiEngine: selectedAiEngine,
            conversationId: newConversation.id,
            role: "user",
            conversationTitle: title,
            userId: resolvedUserId,
          });
        } else {
          const selectedConv = conversations.find((conv) => conv.id === selectedConversationId);
          if (selectedConv && selectedConv.aiEngine !== selectedAiEngine) {
            const newConversationId = uuidv4();
            const title = buildConversationTitle(trimmed, newConversationId);
            const newConversation = {
              id: newConversationId,
              title,
              messages: [userMessage, aiMessage],
              aiEngine: selectedAiEngine,
              lastActivity: now,
              shareType: 0,
              userId: resolvedUserId,
            };

            setConversations((prev) => [newConversation, ...prev]);
            setSelectedConversationId(newConversation.id);
            setSelectedConversationTitle(title);
            aiMessageIndexRef.current = 1;

            await connectionRef.current.invoke("StartChat", {
              newMessage: trimmed,
              aiEngine: selectedAiEngine,
              conversationId: newConversation.id,
              role: "user",
              conversationTitle: title,
              userId: resolvedUserId,
            });
          } else {
            let aiMessageIndex = null;
            setConversations((prevConversations) => {
              const updated = prevConversations.map((conv) => {
                if (conv.id === selectedConversationId) {
                  const updatedMessages = [...conv.messages, userMessage, aiMessage];
                  aiMessageIndex = updatedMessages.length - 1;
                  return { ...conv, messages: updatedMessages, lastActivity: now };
                }
                return conv;
              });

              const current = updated.find((c) => c.id === selectedConversationId);
              const others = updated.filter((c) => c.id !== selectedConversationId);
              aiMessageIndexRef.current = aiMessageIndex;
              return current ? [current, ...others] : updated;
            });

            await connectionRef.current.invoke("StartChat", {
              newMessage: trimmed,
              aiEngine: selectedAiEngine,
              conversationId: selectedConversationId,
              role: "user",
              conversationTitle: selectedConversationTitle,
              userId: resolvedUserId,
            });
          }
        }
      } catch (error) {
        console.error("Error sending message:", error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [
      conversations,
      homeState.userProfile?.userId,
      selectedAiEngine,
      selectedConversationId,
      selectedConversationTitle,
      userId,
    ]
  );

  const handleSaveConversationTitle = useCallback(
    async (id, newTitle) => {
      try {
        const chatbotPut = {
          conversationId: id,
          conversationTitle: newTitle,
        };
        await api.post("/api/chatbot/UpdateConversationTitle", chatbotPut);
        setConversations((prev) =>
          prev.map((conv) => (conv.id === id ? { ...conv, title: newTitle } : conv))
        );
        if (selectedConversationId === id) {
          setSelectedConversationTitle(newTitle);
        }
      } catch (error) {
        console.error("Error updating conversation title:", error);
      }
    },
    [selectedConversationId]
  );

  const handleDeleteConversation = useCallback(async (id) => {
    try {
      const chatbotDelete = { conversationId: id };
      await api.post("/api/chatbot/DeleteConversation", chatbotDelete);
      setConversations((prev) => prev.filter((conv) => conv.id !== id));
      if (selectedConversationId === id) {
        setSelectedConversationId(null);
        setSelectedConversationTitle(null);
      }
    } catch (error) {
      console.error("Error deleting conversation:", error);
    }
  }, [selectedConversationId]);

  const handleShareConversation = useCallback(async (id, shareType) => {
    try {
      const sharingChatbotDto = { sharingId: id, sharingOption: shareType };
      await api.put("/api/chatbot/UpdateShareType", sharingChatbotDto);
      setConversations((prev) =>
        prev.map((conv) => (conv.id === id ? { ...conv, shareType } : conv))
      );
    } catch (error) {
      console.error("Error updating share type:", error);
    }
  }, []);

  return {
    conversations,
    selectedConversation,
    selectedConversationId,
    selectedConversationTitle,
    messages,
    loading,
    historyLoading,
    aiEngines,
    groupedEngines,
    engineFamilies,
    selectedEngineFamily,
    selectedAiEngine,
    activeTab,
    connectionStatus,
    isReadOnly,
    setActiveTab,
    handleEngineFamilyChange,
    handleAiEngineChange,
    handleSelectConversation,
    handleNewConversation,
    handleSaveConversationTitle,
    handleDeleteConversation,
    handleShareConversation,
    sendMessage,
    refreshConversations: fetchFilteredConversations,
  };
}
